import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/components/loading';
import { Center } from '@/components/center';
import { PendingInvitationsBox } from '@/components/pending-invitations-box';
import { AppLayout } from '@/components/app-layout';
import { useMeInvitations } from '@/hooks/use-me-invitations';
import { useSessionQuery } from '@/api/login';
import {
  useAcceptMembershipInvitationMutation,
  useDeclineMembershipInvitationMutation,
  useAcceptOrganisationInvitationMutation,
  useDeclineOrganisationInvitationMutation,
} from '@/api/user';
import { useAcceptShareMutation, useDeclineShareMutation } from '@/api/share';
import { useToast } from '@kinesis/bungle';
import { launch, sharedPathnameFor } from '@/services/launch';
import { InvitationError } from '@/components/invitation-error';

const Invitations = () => {
  const toast = useToast('globalTop');
  const { data: session, isSuccess, isLoading } = useSessionQuery();

  const { data: invitations, isLoading: invitationsLoading } =
    useMeInvitations();

  const [invitationAccept, { isLoading: isAccepting }] =
    useAcceptMembershipInvitationMutation();
  const [invitationDecline, { isLoading: isDeclining }] =
    useDeclineMembershipInvitationMutation();
  const [organisationInvitationAccept, { isLoading: isAcceptingDiplomat }] =
    useAcceptOrganisationInvitationMutation();
  const [organisationInvitationDecline, { isLoading: isDecliningDiplomat }] =
    useDeclineOrganisationInvitationMutation();
  const [shareAccept, { isLoading: isAcceptingShare }] =
    useAcceptShareMutation();
  const [shareDecline, { isLoading: isDecliningShare }] =
    useDeclineShareMutation();
  const [showInvitationError, setShowInvitationError] = useState(false);

  const onAcceptInvitation = useCallback(
    async (token: string) => {
      const invitation = invitations.find((i) => i.token === token);

      // A disclosure share: accepting opens the disclosure itself, rather
      // than joining anything.
      if (invitation?.namespace === 'disclosure') {
        try {
          const accepted = await shareAccept(token).unwrap();
          const pathname = sharedPathnameFor(accepted.object);
          if (pathname) {
            window.location.assign(pathname);
          }
        } catch (e) {
          toast('Error accepting share.', {
            variant: 'error',
            duration: 6000,
          });
        }
        return;
      }

      if (invitation && !invitation.canBeAccepted) {
        return setShowInvitationError(true);
      }

      // A diplomat row is accepted through friend by its token; whether the
      // organisation's SSO mandate refuses this login is answered here, and
      // the answer is the same dialog the friend path raises up front.
      if (invitation?.source === 'diplomat') {
        try {
          await organisationInvitationAccept(token).unwrap();
        } catch (e) {
          if ((e as any)?.data?.code === 'invitation-sso-mandated') {
            return setShowInvitationError(true);
          }
          toast('Error accepting invitation.', {
            variant: 'error',
            duration: 6000,
          });
        }
        return;
      }

      try {
        await invitationAccept(token).unwrap();
      } catch (e) {
        toast('Error accepting invitation.', {
          variant: 'error',
          duration: 6000,
        });
      }
    },
    [
      invitationAccept,
      organisationInvitationAccept,
      shareAccept,
      invitations,
      toast,
    ],
  );

  const onDeclineInvitation = useCallback(
    async (token: string) => {
      const invitation = invitations.find((i) => i.token === token);
      try {
        if (invitation?.namespace === 'disclosure') {
          await shareDecline(token).unwrap();
        } else if (invitation?.source === 'diplomat') {
          await organisationInvitationDecline(token).unwrap();
        } else {
          await invitationDecline(token).unwrap();
        }
      } catch (e) {
        toast('Error declining invitation.', {
          variant: 'error',
          duration: 6000,
        });
      }
    },
    [
      invitationDecline,
      organisationInvitationDecline,
      shareDecline,
      invitations,
      toast,
    ],
  );
  const navigate = useNavigate();

  const handleDone = useCallback(() => {
    if (session?.orphan) {
      return navigate('/registration/complete');
    } else {
      launch(session?.target, true);
    }
  }, [navigate, session?.orphan, session?.target]);

  if (!isLoading && isSuccess) {
    if (!invitationsLoading) {
      return (
        <AppLayout>
          <>
            {showInvitationError && (
              <InvitationError onClose={() => setShowInvitationError(false)} />
            )}
            <Center>
              <PendingInvitationsBox
                onAccept={onAcceptInvitation}
                onDecline={onDeclineInvitation}
                onDone={handleDone}
                invitations={invitations}
                isLoading={
                  isAccepting ||
                  isDeclining ||
                  isAcceptingDiplomat ||
                  isDecliningDiplomat ||
                  isAcceptingShare ||
                  isDecliningShare
                }
              />
            </Center>
          </>
        </AppLayout>
      );
    } else {
      return <Loading />;
    }
  }

  return null;
};

export { Invitations };
